import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LockKeyhole, ShieldCheck } from "lucide-react";
import { AuthField, AuthSplitLayout } from "@/components/AuthSplitLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export default function SetPasswordPage() {
  const { user, applySession, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  async function submit(event) {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setError("");
    setIsSaving(true);
    try {
      const response = await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      applySession(response.data);
      navigate("/", { replace: true });
    } catch (requestError) {
      setError(formatApiError(requestError));
      setIsSaving(false);
    }
  }

  return (
    <AuthSplitLayout
      testId="set-password-page"
      statement="Secure your account before you continue."
      support="You signed in with a temporary password issued by TAG. Replace it with a private password to unlock your workspace."
      heading="Create your own password"
      description={`${user?.first_name ? `${user.first_name}, ` : ""}choose a password only you know. At least 8 characters.`}
      footer={
        <button
          className="login-link-button login-return"
          data-testid="set-password-logout-button"
          type="button"
          onClick={logout}
        >
          Sign out instead
        </button>
      }
    >
      <form className="login-form" onSubmit={submit}>
        <AuthField id="current-password" label="Temporary password" icon={ShieldCheck}>
          <input
            data-testid="set-password-current-input"
            id="current-password"
            type="password"
            placeholder="Password issued by TAG"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            required
            autoComplete="current-password"
          />
        </AuthField>
        <AuthField id="new-password" label="New password" icon={LockKeyhole}>
          <input
            data-testid="set-password-new-input"
            id="new-password"
            type="password"
            placeholder="At least 8 characters"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            required
            minLength="8"
            autoComplete="new-password"
          />
        </AuthField>
        <AuthField id="confirm-password" label="Confirm new password" icon={LockKeyhole}>
          <input
            data-testid="set-password-confirm-input"
            id="confirm-password"
            type="password"
            placeholder="Repeat your new password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            required
            minLength="8"
            autoComplete="new-password"
          />
        </AuthField>
        {error && (
          <p className="form-message error-message" data-testid="set-password-error">{error}</p>
        )}
        <button className="login-submit" data-testid="set-password-submit-button" disabled={isSaving} type="submit">
          {isSaving ? "Saving…" : "Save password and continue"}
        </button>
      </form>
    </AuthSplitLayout>
  );
}
